import "./StockPageTVChart.scss";

import classNames from "classnames";
import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";

import withAdvancedChart from "../../../../../hocs/withAdvancedChart";
import withDeviceType from "../../../../../hocs/withDeviceType";
import withTranslations from "../../../../../hocs/withTranslations";
import {
  hasTvFullScreenQuery,
  withTvFullScreenSearch
} from "../../../../../lib/tvFullScreenQuery";
import CustomSimpleChart from "../../../common/CustomSimpleChart";
import Loader from "../../../common/Loader";
import TvChartBridge from "../../../common/TvChartBridge";

function StockPageTVChart({
  stock,
  stockLoading,
  stockId,
  marketId,
  onRefreshEnded,
  isMobile,
  advancedChartVisible,
  setAdvancedChartVisible,
  translate
}) {
  const history = useHistory();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [inPlaceFullscreen, setInPlaceFullscreen] = useState(
    () => !isMobile && hasTvFullScreenQuery(location.search)
  );
  const [viewportHeight, setViewportHeight] = useState(
    typeof window !== "undefined" ? window.innerHeight : 500
  );

  const syncTvFullScreenQuery = useCallback(
    enabled => {
      const nextSearch = withTvFullScreenSearch(location.search, enabled);
      if (nextSearch === (location.search || "")) {
        return;
      }

      history.replace({
        pathname: location.pathname,
        search: nextSearch,
        hash: location.hash
      });
    },
    [history, location.hash, location.pathname, location.search]
  );

  useEffect(() => {
    if (!hasTvFullScreenQuery(location.search)) {
      return;
    }

    if (isMobile) {
      setAdvancedChartVisible?.(true);
    } else {
      setInPlaceFullscreen(true);
    }
  }, [isMobile, location.search, setAdvancedChartVisible, stockId]);

  const canShowTvChart = stock?.stock && stock?.latest_quote_datetime_utc;

  useEffect(() => {
    if (!canShowTvChart) {
      onRefreshEnded?.();
    }
  }, [canShowTvChart]);

  useEffect(() => {
    const shouldTrackViewport =
      (isMobile && advancedChartVisible) || inPlaceFullscreen;

    if (!shouldTrackViewport) {
      return undefined;
    }

    const onResize = () => setViewportHeight(window.innerHeight);
    onResize();
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [isMobile, advancedChartVisible, inPlaceFullscreen]);

  useEffect(() => {
    return () => {
      setAdvancedChartVisible?.(false);
    };
  }, [setAdvancedChartVisible]);

  const handleLoadingStarted = useCallback(() => {
    setLoading(true);
  }, []);

  const handleLoadingEnded = useCallback(() => {
    setLoading(false);
    onRefreshEnded?.();
  }, []);

  const handleCloseAdvancedChart = useCallback(() => {
    setAdvancedChartVisible(false);
    syncTvFullScreenQuery(false);
  }, [setAdvancedChartVisible, syncTvFullScreenQuery]);

  const handleToggleInPlaceFullscreen = useCallback(() => {
    const next = !inPlaceFullscreen;
    setInPlaceFullscreen(next);
    syncTvFullScreenQuery(next);
  }, [inPlaceFullscreen, syncTvFullScreenQuery]);

  let chartContent = null;

  if (canShowTvChart) {
    if (isMobile) {
      // Same as pre-migration web TVChart: simple chart inline; expand opens
      // the advanced chart as a fullscreen layer (via advancedChartVisible).
      chartContent = (
        <React.Fragment>
          <CustomSimpleChart
            stockId={stock.stock.id}
            showVolumeStudy={true}
            translate={translate}
          />
          {advancedChartVisible ? (
            <div className="StockPageTVChart__advanced-overlay is-on-full-screen">
              <TvChartBridge
                key={stock.stock.id}
                stock={stock.stock}
                latestQuoteDatetimeUtc={stock.latest_quote_datetime_utc}
                height={viewportHeight}
                showStocksListButton
                showFullscreenButton
                showLeadingFullscreenButton
                isFullscreenView
                onOpenFullscreenChart={handleCloseAdvancedChart}
              />
            </div>
          ) : null}
        </React.Fragment>
      );
    } else {
      chartContent = (
        <TvChartBridge
          key={stock.stock.id}
          onRefreshStarted={handleLoadingStarted}
          onRefreshEnded={handleLoadingEnded}
          stock={stock.stock}
          latestQuoteDatetimeUtc={stock.latest_quote_datetime_utc}
          height={inPlaceFullscreen ? viewportHeight : undefined}
          showFullscreenButton
          showStocksListButton
          isFullscreenView={inPlaceFullscreen}
          onOpenFullscreenChart={handleToggleInPlaceFullscreen}
        />
      );
    }
  }

  return (
    <div
      className={classNames("StockPageTVChart__wrapper", {
        "StockPageTVChart__advanced-overlay": !isMobile && inPlaceFullscreen,
        "is-on-full-screen": !isMobile && inPlaceFullscreen
      })}
    >
      {stockLoading || (!isMobile && loading) ? (
        <Loader className={"StockPageTVChart__loader"} />
      ) : (
        chartContent
      )}
    </div>
  );
}

export default withDeviceType(
  withTranslations(withAdvancedChart(StockPageTVChart))
);
